import { useStoreConfiguration } from '../../store/configuration/configuration';
import safeFetch from '../../utils/safeFetch';
import {
  buildProductStructuredDataPayload,
  extractVariantsFromContainer,
  getProductGroupValues,
} from './schemaProductRichSnippet';

export const schemaProductRichSnippetMethods = {
  getSchemaCurrency() {
    const configStore = useStoreConfiguration();

    return (
      this.shopConfig?.currencyCode ||
      configStore?.config?.pt_format_currencyCode ||
      window._CAAS_config?.currencyCode ||
      'EUR'
    );
  },
  logSchemaDebug() {
    return;
  },
  syncProductStructuredDataScript(json) {
    if (typeof document === 'undefined') {
      return;
    }

    const scriptId = 'schema-product-rich-snippet';
    let script = document.getElementById(scriptId);

    if (!json) {
      if (script) {
        script.remove();
        this.logSchemaDebug('schema script removed from head');
      }
      return;
    }

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
      this.logSchemaDebug('schema script inserted into head');
    } else {
      this.logSchemaDebug('schema script updated in head');
    }

    script.textContent = json;
  },
  async collectVariantsFromAllTables() {
    if (!this.emTable) {
      this.logSchemaDebug('collectVariantsFromAllTables aborted: emTable is missing', null, 'warn');
      return [];
    }

    const currency = this.getSchemaCurrency();
    this.logSchemaDebug('collectVariantsFromAllTables started', {
      currency,
      hasRadios: Boolean(this.radios),
    });
    const variants = [];
    const processedSkus = new Set();
    const skippedVariants = [];
    const tableUrls = new Set();

    variants.push(
      ...extractVariantsFromContainer({
        container: this.emTable,
        currency,
        processedSkus,
        skippedVariants,
        baseUrl: window.location.href,
      }),
    );

    if (this.radios) {
      this.radios.querySelectorAll('.orderDataItem').forEach((radio) => {
        if (radio?.value) {
          tableUrls.add(radio.value);
        }
      });
    }

    for (const tableUrl of tableUrls) {
      try {
        const response = await safeFetch(tableUrl);
        if (!response.ok) {
          skippedVariants.push({ reason: 'table-fetch-failed', tableUrl, status: response.status });
          continue;
        }

        const tableContent = await response.text();
        const wrapper = document.createElement('div');
        wrapper.innerHTML = tableContent;

        const tableRoot = wrapper.querySelector('.em-table-slide') || wrapper;
        variants.push(
          ...extractVariantsFromContainer({
            container: tableRoot,
            currency,
            processedSkus,
            skippedVariants,
            baseUrl: window.location.href,
          }),
        );
      } catch (error) {
        skippedVariants.push({
          reason: 'table-fetch-exception',
          tableUrl,
          message: error?.message || String(error),
        });
      }
    }

    this.logSchemaDebug('collectVariantsFromAllTables finished', {
      variantCount: variants.length,
      tableCount: tableUrls.size,
      skippedCount: skippedVariants.length,
      skippedVariants,
    });

    return variants;
  },
  async updateProductStructuredData() {
    this.schemaUpdateRequestId += 1;
    const requestId = this.schemaUpdateRequestId;

    this.logSchemaDebug('updateProductStructuredData invoked', {
      isShop: this.isShop,
      activeTab: this.activeTab,
      hasEmTable: Boolean(this.emTable),
    });

    if (!this.isShop) {
      this.schemaProductRichSnippetJson = null;
      this.syncProductStructuredDataScript(null);
      this.logSchemaDebug('schema cleared: not a shop context');
      return;
    }

    const productGroupValues = getProductGroupValues(this.shopConfig);
    const hasVariant = await this.collectVariantsFromAllTables();

    if (requestId !== this.schemaUpdateRequestId) {
      this.logSchemaDebug('schema update skipped due to newer request', { requestId }, 'warn');
      return;
    }

    this.logSchemaDebug('schema base data collected', {
      name: productGroupValues.name,
      description: productGroupValues.description,
      groupId: productGroupValues.groupId,
      groupIds: productGroupValues.groupIds,
      isMultiGroup: productGroupValues.isMultiGroup,
      hasVariantCount: hasVariant.length,
    });

    const payload = buildProductStructuredDataPayload(productGroupValues, hasVariant);

    if (!payload) {
      this.schemaProductRichSnippetJson = null;
      this.syncProductStructuredDataScript(null);
      this.logSchemaDebug(
        'schema not generated due to missing required data',
        {
          missingName: !productGroupValues.name,
          hasVariantCount: hasVariant.length,
          isMultiGroup: productGroupValues.isMultiGroup,
        },
        'warn',
      );
      return;
    }

    this.schemaProductRichSnippetJson = JSON.stringify(payload);
    this.syncProductStructuredDataScript(this.schemaProductRichSnippetJson);

    this.logSchemaDebug('schema generated successfully', {
      productGroupID: productGroupValues.groupId || productGroupValues.name,
      groupIds: productGroupValues.groupIds,
      isMultiGroup: productGroupValues.isMultiGroup,
      usesGraph: Boolean(payload['@graph']),
      hasVariantCount: hasVariant.length,
    });
  },
};
